import {
  importImageEffects,
  isInactiveLegacyDefault,
  isLegacyImage,
  legacyImageFields,
} from "./legacy-import-image";
import { legacyLayers } from "./legacy-import-groups";
import { assertProjectDocument, isRecord } from "../document/model";
import type { EngineDocument, SceneNode } from "../scene/model";
import {
  assertNativeDocument,
  createNativeNode,
  emptyNativeDocument,
} from "./document";
import { createTextNode } from "./text";
import { importLegacyPath } from "./legacy-import-path";
import { pathNode } from "./vector-path";
import {
  importText,
  legacyColor,
  legacyPaint,
  legacyNumber,
  unsupported,
} from "./legacy-import-text";

// This boundary understands serialized old files, never renderer instances.
const common = `legacyGroupPath dimensionAspectLocked exportFileName originalFileName type version originX originY left top width height fill stroke strokeWidth strokeDashArray strokeLineCap strokeDashOffset strokeLineJoin strokeUniform strokeMiterLimit scaleX scaleY angle flipX flipY opacity shadow visible backgroundColor fillRule paintFirst globalCompositeOperation skewX skewY objectRole objectId customName layerGroupId layerGroupName layerGroupCollapsed layerLocked clipToBelow selectable evented hasControls hasBorders lockMovementX lockMovementY lockRotation lockScalingX lockScalingY lockUniScaling objectCaching dirty perPixelTargetFind excludeFromExport`;
const fields: Record<string, string> = {
  rect: "rx ry",
  circle: "radius startAngle endAngle",
  ellipse: "rx ry",
  triangle: "",
  image: legacyImageFields,
  "warped-image": legacyImageFields,
  line: "x1 x2 y1 y2",
  path: "path pathOffset bezierAnchors bezierClosed shapeKind vectorStart vectorEnd",
  textbox:
    "text fontFamily fontSize fontWeight fontStyle underline overline linethrough textAlign lineHeight textBackgroundColor charSpacing styles direction path pathStartOffset pathSide pathAlign minWidth splitByGrapheme",
  text: "",
  "i-text": "",
};
fields.text = fields["i-text"] = fields.textbox!;
function empty(v: unknown): boolean {
  return (
    v == null ||
    v === false ||
    v === 0 ||
    v === "" ||
    (Array.isArray(v) && !v.length)
  );
}
function validate(d: Record<string, unknown>) {
  const type = String(d.type);
  if (!Object.hasOwn(fields, type)) unsupported(`тип слоя «${type}»`);
  const allowed = new Set((common + " " + fields[type]).split(" "));
  for (const [key, value] of Object.entries(d))
    if (
      !allowed.has(key) &&
      !empty(value) &&
      !isInactiveLegacyDefault(key, value)
    )
      unsupported(`свойство «${key}»`);
  for (const key of ["originalFileName", "exportFileName"])
    if (d[key] != null && typeof d[key] !== "string")
      unsupported(`имя файла «${key}»`);
  for (const key of [
    "flipX",
    "flipY",
    "skewX",
    "skewY",
    "cropX",
    "cropY",
    "strokeDashOffset",
    "backgroundColor",
  ])
    if (!empty(d[key])) unsupported(key);
  if (
    d.dimensionAspectLocked !== undefined &&
    typeof d.dimensionAspectLocked !== "boolean"
  )
    unsupported("связь размеров слоя");
  if (d.fillRule && d.fillRule !== "nonzero") unsupported("правило заливки");
  if (d.paintFirst && d.paintFirst !== "fill") unsupported("порядок обводки");
  if (
    d.layerLocked !== true &&
    [
      "lockMovementX",
      "lockMovementY",
      "lockRotation",
      "lockScalingX",
      "lockScalingY",
      "lockUniScaling",
    ].some((k) => d[k])
  )
    unsupported("отдельные блокировки трансформации");
  if (d.layerLocked !== true && (d.selectable === false || d.evented === false))
    unsupported("особые правила выбора слоя");
  if (
    type === "circle" &&
    (legacyNumber(d, "startAngle", 0) !== 0 ||
      legacyNumber(d, "endAngle", 360) !== 360)
  )
    unsupported("дуга окружности");
}
function origin(v: unknown, axis: "x" | "y") {
  if (v === "center") return 0;
  if (v === (axis === "x" ? "right" : "bottom")) return -0.5;
  if (v == null || v === (axis === "x" ? "left" : "top")) return 0.5;
  if (typeof v === "number" && Number.isFinite(v)) return 0.5 - v;
  return unsupported("начало координат слоя");
}

/** Resolve media outside this pure conversion. All-or-nothing capability validation. */
export function importLegacyDocument(
  input: unknown,
  sources: ReadonlyMap<string, string> = new Map(),
): EngineDocument {
  assertProjectDocument(input);
  if (input.screens.length !== 1) unsupported("несколько страниц");
  const screen = input.screens[0]!,
    document = emptyNativeDocument(),
    page = document.pages[0]!;
  page.id = screen.id || "imported-page";
  document.activePageId = page.id;
  page.name = screen.name || "Импортированный проект";
  page.exportName = screen.fileName || "index";
  page.size = {
    width: Number(screen.width ?? 500),
    height: Number(screen.height ?? 500),
  };
  page.unit = String(screen.measurementUnit || "px");
  page.workspaceColor = String(screen.pasteboardColor || "#ecf0f1");
  page.backgroundVisible = screen.backgroundVisible !== false;
  const media = new Map<string, string>(),
    ids = new Set<string>();
  page.nodes = legacyLayers(screen.layers || []).map((layer, index) => {
    const d = layer.data,
      num = (k: string, fallback = 0) => legacyNumber(d, k, fallback);
    try {
      validate(d);
      const type = String(d.type),
        sx = num("scaleX", 1),
        sy = num("scaleY", 1),
        sw = num("strokeWidth", 1);
      if (sx <= 0 || sy <= 0) unsupported("отрицательный масштаб");
      const w = num("width"),
        h = num("height"),
        width = Math.max(1, w * sx),
        height = Math.max(1, h * sy);
      const angle = num("angle"),
        radians = (angle * Math.PI) / 180;
      // Old origins refer to the box including stroke, even when stroke is null.
      const dx =
          (w * sx + sw * (d.strokeUniform ? 1 : sx)) * origin(d.originX, "x"),
        dy =
          (h * sy + sw * (d.strokeUniform ? 1 : sy)) * origin(d.originY, "y");
      const x = num("left") + Math.cos(radians) * dx - Math.sin(radians) * dy,
        y = num("top") + Math.sin(radians) * dx + Math.cos(radians) * dy;
      const id =
        typeof d.objectId === "string" && d.objectId
          ? d.objectId
          : `import-${index + 1}`;
      if (ids.has(id)) unsupported("повторяющийся ID слоя");
      ids.add(id);
      let node: SceneNode;
      if (["textbox", "text", "i-text"].includes(type)) {
        node = createTextNode(id, 0, 0, true, width, height);
        node.text = {
          ...importText(d),
          layoutWidth: Math.max(1, w),
          layoutHeight: Math.max(1, h),
        };
      } else if (type === "path") {
        node = importLegacyPath(id, d.path);
        node.geometry.width = width;
        node.geometry.height = height;
      } else if (type === "line") {
        node = pathNode(id, [
          { x: 0, y: 0 },
          { x: width, y: height },
        ]);
        const fx = num("x1") > num("x2") ? -1 : 1,
          fy = num("y1") > num("y2") ? -1 : 1;
        node.geometry.vector = {
          anchors: [
            { x: w ? -fx / 2 : 0, y: h ? -fy / 2 : 0 },
            { x: w ? fx / 2 : 0, y: h ? fy / 2 : 0 },
          ],
          closed: false,
          arrow: false,
        };
      } else {
        node = createNativeNode(
          id,
          type === "rect"
            ? "rectangle"
            : isLegacyImage(d)
              ? "image"
              : type === "triangle"
                ? "polygon"
                : "ellipse",
          x,
          y,
          width,
          height,
        );
      }
      node.transform = {
        x,
        y,
        rotation: angle,
        originX: "center",
        originY: "center",
      };
      const role =
        node.kind === "text"
          ? "decorText"
          : node.kind === "image"
            ? "decorImage"
            : "decorShape";
      if (d.objectRole && d.objectRole !== role)
        unsupported(`интерактивная роль «${d.objectRole}»`);
      const fill = legacyPaint(d.fill, d),
        stroke = legacyPaint(d.stroke, d);
      if (type === "path") {
        if (fill.alpha && !(node.geometry.vector as { closed: boolean }).closed)
          unsupported("заливку незамкнутого контура");
        if (stroke.alpha && sw && (d.strokeLineJoin ?? "miter") !== "round")
          unsupported("неокруглённое соединение обводки контура");
      }
      node.appearance = {
        fill: fill.color,
        ...(fill.gradient ? { fillGradient: fill.gradient } : {}),
        ...(stroke.gradient ? { strokeGradient: stroke.gradient } : {}),
        opacity: num("opacity", 1),
        visible: d.visible !== false,
        blendMode: String(d.globalCompositeOperation || "source-over"),
      };
      if (fill.alpha !== 1 && node.kind !== "text")
        node.appearance.fillAlpha = fill.alpha;
      if (!["image", "text"].includes(node.kind)) {
        if (stroke.alpha && sx !== sy && !d.strokeUniform)
          unsupported("неравномерно масштабированная обводка");
        const scale = d.strokeUniform ? 1 : sx;
        Object.assign(node.appearance, {
          fillEnabled: fill.alpha > 0 && type !== "line",
          stroke: stroke.color,
          strokeEnabled: stroke.alpha > 0 && sw > 0,
          strokeWidth: sw * scale,
          lineStyle:
            Array.isArray(d.strokeDashArray) && d.strokeDashArray.length
              ? "dashed"
              : "solid",
        });
        if (stroke.alpha !== 1) node.appearance.strokeAlpha = stroke.alpha;
        if (Array.isArray(d.strokeDashArray) && d.strokeDashArray.length)
          node.appearance.strokeDash = d.strokeDashArray.map(
            (v) => Number(v) * scale,
          );
        if (type === "line" || type === "path")
          node.appearance.lineCap = String(d.strokeLineCap || "butt");
      } else if (node.kind === "image" && stroke.alpha && sw)
        unsupported("обводка изображения");
      if (type === "rect" && (num("rx") || num("ry"))) {
        if (num("rx") * sx !== num("ry") * sy)
          unsupported("разные радиусы скругления");
        node.geometry.cornerRadius = num("rx") * sx;
      }
      if (isLegacyImage(d)) {
        const src = String(d.src || ""),
          source = sources.get(src) || src;
        let assetId = media.get(source);
        if (!assetId) {
          assetId = `import-image-${media.size + 1}`;
          media.set(source, assetId);
          document.assets.push({ id: assetId, kind: "image", source });
        }
        node.media.image = assetId;
        node = importImageEffects(node, d);
      }
      if (d.shadow) {
        if (!isRecord(d.shadow)) unsupported("тень");
        const s = d.shadow,
          color = legacyColor(s.color);
        if (
          sx !== sy ||
          angle ||
          stroke.alpha > 0 ||
          s.nonScaling === true ||
          s.affectStroke === true
        )
          unsupported("тень масштабированного слоя или обводки");
        node.effects = {
          ...node.effects,
          shadow: true,
          shadowStyle: {
            color: color.color,
            alpha: color.alpha,
            blur: legacyNumber(s, "blur") * sx,
            x: legacyNumber(s, "offsetX") * sx,
            y: legacyNumber(s, "offsetY") * sx,
          },
        };
      }
      node.layer = {
        name: String(d.customName || layer.name || node.layer.name),
        locked: d.layerLocked === true,
        ...(d.legacyGroupPath ? { legacyGroupPath: d.legacyGroupPath } : {}),
      };
      for (const [from, to] of [
        ["layerGroupId", "groupId"],
        ["layerGroupName", "groupName"],
        ["layerGroupCollapsed", "groupCollapsed"],
        ["clipToBelow", "clipToBelow"],
        ["dimensionAspectLocked", "aspectLocked"],
        ["exportFileName", "exportName"],
        ["originalFileName", "originalName"],
      ])
        if (d[from!] != null) node.layer[to!] = d[from!];
      return node;
    } catch (error) {
      throw new Error(
        `Слой ${index + 1} «${layer.name || d.customName || d.type}»: ${(error as Error).message}`,
        { cause: error },
      );
    }
  });
  assertNativeDocument(document);
  return document;
}
